import { Button, Grid, GridItem } from "@chakra-ui/react";
import { AirplaneTakeOff, AirplaneLanding } from "@airhelp/icons";
import { useCallback, useState, useEffect } from "react";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { useDeviceSize, useIsTouchscreen } from "hooks";
import { HERO_CHECK_COMPENSATION_EVENT, HERO_CHECK_COMPENSATION_EVENT_UA } from "modules/home-page/trackingConsts";
import type { SbEditableComponentProps } from "types/storyblok";
import { getPageUrl } from "utils/getPageUrl";
import { trackCustomEvent, trackUAEvent } from "utils/tracking";
import type { Airport } from "@airhelp/react";
import uniqueId from "lodash/uniqueId";
import type { AutocompleteFormProps, ConnectingAirport } from "../AutocompleteForm.d";
import autocompleteFormStyles from "../AutocompleteForm.styles";
import { AutocompleteInput } from "../AutocompleteInput";
import { FUNNEL_AB_PARAMETER } from "../consts";
import { ConnectingAirportInputs } from "./components/ConnectingAirportInputs";
import { CONNECTING_FLIGHT_ADD_CLICK, CONNECTING_FLIGHT_ADD_CLICK_EVENT_UA } from "./gaEvents";

export const AutocompleteMultilegForm = ({
  ctaVariant = "secondary",
  buttonText,
  variant = "wide",
  sbEditable,
  isMultiLeg = false,
  withDefaultField = false,
  departureAirport,
  arrivalAirport,
  setDepartureAirport,
  setArrivalAirport,
}: SbEditableComponentProps<AutocompleteFormProps>) => {
  const translations = useTranslations();
  const language = useLanguage();

  const [connections, setConnections] = useState<Array<ConnectingAirport | Pick<ConnectingAirport, "id">>>([]);
  const [funnelAbParameter, setFunnelAbParameter] = useState("");
  const { isMobile } = useDeviceSize();
  const showConnections = isMobile && isMultiLeg;
  const showAddConnectionButton = isMobile && isMultiLeg && connections.length < 6;
  const isTouchscreen = useIsTouchscreen();
  const styles = autocompleteFormStyles(variant);
  const disabledAirports = [...(connections as Airport[]), departureAirport, arrivalAirport];

  const addConnection = () => {
    trackUAEvent(CONNECTING_FLIGHT_ADD_CLICK_EVENT_UA);
    trackCustomEvent(CONNECTING_FLIGHT_ADD_CLICK);
    setConnections((connections) => [...connections, { id: uniqueId() }]);
  };
  const removeConnection = (id: string) =>
    setConnections((connections) => connections.filter((connection) => connection.id !== id));

  const setConnection = (airport: Airport, id: string) => {
    setConnections((connections) => {
      const newConnections = [...connections];
      const index = newConnections.findIndex((connection) => connection.id === id);
      newConnections[index] = { id, ...airport };
      return newConnections;
    });
  };

  useEffect(() => {
    if (withDefaultField) addConnection();
  }, [withDefaultField]);

  const getWebappPath = useCallback(() => {
    const departureAirportValue = departureAirport ? departureAirport.iata : "";
    const arrivalAirportValue = arrivalAirport ? arrivalAirport.iata : "";
    const connectionsIatas = connections.reduce((acc, connection) => {
      if ("iata" in connection) {
        return [...acc, connection.iata];
      }
      return acc;
    }, []);

    const connectionsUrlParam =
      connectionsIatas.length > 0 && isMultiLeg ? `&connectingAirportsIatas=${connectionsIatas.join(",")}` : "";

    return `${getPageUrl(
      language,
      "webappNewClaim"
    )}&departureAirportIata=${departureAirportValue}&arrivalAirportIata=${arrivalAirportValue}${connectionsUrlParam}${funnelAbParameter}`;
  }, [arrivalAirport, departureAirport, language, funnelAbParameter, connections, isMultiLeg]);

  const onClickHandler = () => {
    if (window?.funnelAb) setFunnelAbParameter(FUNNEL_AB_PARAMETER);
    trackUAEvent(HERO_CHECK_COMPENSATION_EVENT_UA);
    trackCustomEvent(HERO_CHECK_COMPENSATION_EVENT);
  };

  return (
    <Grid templateColumns={styles.templateColumns} columnGap={2} rowGap={3} {...sbEditable}>
      <AutocompleteInput
        placeholder={translations("departure_airport")}
        setAirportSelected={setDepartureAirport}
        autoFocus={!isTouchscreen}
        instanceId="departure-input"
        dataTestId="hero-departure-input"
        key={`hero-departure-input-autofocus-${!isTouchscreen}`}
        icon={<AirplaneTakeOff boxSize={6} />}
        disabledAirports={disabledAirports}
        value={departureAirport ? { value: departureAirport } : null}
      />
      {showConnections && (
        <ConnectingAirportInputs
          disabledAirports={disabledAirports}
          connections={connections}
          onRemove={removeConnection}
          addConnection={addConnection}
          setConnection={setConnection}
          showAddConnectionButton={showAddConnectionButton}
        />
      )}
      <AutocompleteInput
        placeholder={translations("arrival_airport")}
        setAirportSelected={setArrivalAirport}
        instanceId="destination-input"
        dataTestId="hero-destination-input"
        disabledAirports={disabledAirports}
        icon={<AirplaneLanding boxSize={6} />}
        value={arrivalAirport ? { value: arrivalAirport } : null}
      />
      <GridItem colSpan={styles.buttonColSpan}>
        <Button
          as="a"
          variant={ctaVariant}
          title={translations("check_compensation")}
          href={getWebappPath()}
          h="50px"
          mt={{ base: 4, md: 0 }}
          onClick={onClickHandler}
          data-testid="hero-cta-button"
          w="full"
        >
          {buttonText || translations("check_compensation")}
        </Button>
      </GridItem>
    </Grid>
  );
};
